import { inject, Injectable, signal } from '@angular/core';
import {
  createInitialPricing,
  findPricing,
  getCurrentSchoolYear,
  Module,
  School,
} from '@awo-care/shared-core';
import { SchoolService } from './schools.service';
import {
  findReactivationConflict,
  getStaleLunchReferenceUpdates,
  isModuleActive,
} from './module.utils';
import { addDoc, collection, deleteDoc, deleteField, doc, getDocs, query, serverTimestamp, setDoc, where, writeBatch } from 'firebase/firestore';
import { Auth } from 'firebase/auth';
import { FIREBASE_AUTH } from '@awo-care/shared-firebase';

@Injectable({
  providedIn: 'root',
})
export class EmployeeSchoolService extends SchoolService {
  private auth = inject(FIREBASE_AUTH);
  showInactive = signal<boolean>(false);

  private getAuditMetadata() {
    const user = this.auth.currentUser;
    return {
      lastModifiedBy: user?.uid || 'system',
      lastModifiedByEmail: user?.email || 'system@awo-care.org',
      lastModifiedSource: 'employee-ui' as const,
    };
  }

  async createSchool(school: School): Promise<string> {
    try {
      if (!school.schoolName || !school.address) {
        throw new Error('Missing required school information');
      }

      const user = this.auth.currentUser;
      const auditMetadata = this.getAuditMetadata();

      const newSchool: School = {
        ...school,
        isActive: true,
        // Ohne Preisschiene laesst sich an der Schule kein Modul anlegen, und der
        // Anlege-Dialog kann nur bestehende Schienen kopieren (#1076). Eine neue
        // Schule startet deshalb mit einer aktiven Schiene fuer das laufende
        // Schuljahr.
        pricing: school.pricing?.length
          ? school.pricing
          : [
              createInitialPricing(
                getCurrentSchoolYear().year,
                user?.uid || 'system',
              ),
            ],
        costUnit: school.costUnit || '',
        isDemoSchool: school.isDemoSchool ?? false,
        createdBy: user?.uid || 'system',
        createdByEmail: user?.email || 'system@awo-care.org',
        createdOn: serverTimestamp() as never,
        ...auditMetadata,
      };

      const docRef = await addDoc(this.schoolsCollection, newSchool);
      this.schoolsSignal.update((schools) => [
        ...schools,
        { ...newSchool, id: docRef.id },
      ]);

      return docRef.id;
    } catch (error) {
      console.error('Error creating school:', error);
      throw this.handleError(error, 'Error creating school');
    }
  }

  async updateSchool(
    schoolId: string,
    updates: Partial<School>
  ): Promise<void> {
    try {
      const schoolRef = doc(this.schoolsCollection, schoolId);
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      const { id, ...updatesWithoutId } = updates;
      const auditMetadata = this.getAuditMetadata();
      const updatesWithMetadata = { ...updatesWithoutId, ...auditMetadata };

      await setDoc(schoolRef, updatesWithMetadata, { merge: true });

      this.schoolsSignal.update((schools) =>
        schools.map((school) =>
          school.id === schoolId ? { ...school, ...updatesWithMetadata } : school
        )
      );

      if (this.schoolDetails()?.id === schoolId) {
        this.schoolDetailsSignal.update((current) =>
          current ? { ...current, ...updatesWithMetadata } : current
        );
      }
    } catch (error) {
      console.error('Error updating school:', error);
      throw this.handleError(error, 'Error updating school');
    }
  }

  async createModule(
    schoolId: string,
    module: Module,
    schoolYear: number
  ): Promise<string> {
    try {
      const modulesCollection = this.getModulesCollection(schoolId);
      const auditMetadata = this.getAuditMetadata();
      const moduleWithMetadata = {
        ...module,
        schoolYear,
        lastModifiedBy: auditMetadata.lastModifiedBy,
        lastModifiedByEmail: auditMetadata.lastModifiedByEmail,
      };

      const docRef = await addDoc(modulesCollection, moduleWithMetadata);

      const created = { ...moduleWithMetadata, id: docRef.id };
      this.modulesSignal.update((modules) => [...modules, created]);
      this.allModulesSignal.update((modules) => [...modules, created]);

      return docRef.id;
    } catch (error) {
      console.error('Error creating module:', error);
      throw this.handleError(error, 'Error creating module');
    }
  }

  /**
   * Legt mehrere Module in einem Rutsch an (#792).
   *
   * Bewusst als Batch: beim Übernehmen aus einer anderen Schule darf kein
   * halber Import stehenbleiben — sonst legt ein zweiter Versuch die schon
   * angekommenen Module ein zweites Mal an, mit neuer Nummer.
   */
  async createModules(
    schoolId: string,
    modules: Module[],
    schoolYear: number
  ): Promise<string[]> {
    if (!modules.length) return [];
    // Firestore nimmt 500 Schreibvorgänge pro Batch; eine Preisschiene hat
    // gut zwei Dutzend Module, die Grenze ist reine Absicherung.
    if (modules.length > 500) {
      throw new Error('Zu viele Module auf einmal - bitte in Etappen übernehmen.');
    }
    try {
      const modulesCollection = this.getModulesCollection(schoolId);
      const auditMetadata = this.getAuditMetadata();
      const batch = writeBatch(this.firestore);
      const created = modules.map((module) => {
        const docRef = doc(modulesCollection);
        const withMetadata = {
          ...module,
          schoolYear,
          lastModifiedBy: auditMetadata.lastModifiedBy,
          lastModifiedByEmail: auditMetadata.lastModifiedByEmail,
        };
        batch.set(docRef, withMetadata);
        return { ...withMetadata, id: docRef.id };
      });

      await batch.commit();

      this.modulesSignal.update((existing) => [
        ...existing,
        ...created.filter(isModuleActive),
      ]);
      this.allModulesSignal.update((existing) => [...existing, ...created]);

      return created.map((module) => module.id);
    } catch (error) {
      console.error('Error creating modules:', error);
      throw this.handleError(error, 'Error creating modules');
    }
  }

  async updateModule(
    schoolId: string,
    moduleId: string,
    updates: { [K in keyof Module]?: Module[K] | null }
  ): Promise<void> {
    try {
      const moduleRef = doc(this.getModulesCollection(schoolId), moduleId);
      const auditMetadata = this.getAuditMetadata();

      const firestoreUpdates: Record<string, unknown> = {
        lastModifiedBy: auditMetadata.lastModifiedBy,
        lastModifiedByEmail: auditMetadata.lastModifiedByEmail,
      };
      const localUpdates: Record<string, unknown> = { ...firestoreUpdates };
      for (const [key, value] of Object.entries(updates)) {
        if (value === null) {
          firestoreUpdates[key] = deleteField();
          localUpdates[key] = undefined;
        } else {
          firestoreUpdates[key] = value;
          localUpdates[key] = value;
        }
      }

      await setDoc(moduleRef, firestoreUpdates, { merge: true });

      const applyPatch = (module: Module): Module => {
        if (module.id !== moduleId) return module;
        const next: Record<string, unknown> = { ...module, ...localUpdates };
        for (const key of Object.keys(localUpdates)) {
          if (localUpdates[key] === undefined) {
            delete next[key];
          }
        }
        return next as unknown as Module;
      };

      this.allModulesSignal.update((modules) => modules.map(applyPatch));
      // modulesSignal-Vertrag (nur aktive) erzwingen: Deaktivierte fallen
      // raus, ein reaktiviertes Modul kommt aus allModulesSignal dazu.
      this.modulesSignal.update((modules) => {
        const patched = modules.map(applyPatch);
        if (
          updates.isActive === true &&
          !patched.some((m) => m.id === moduleId)
        ) {
          const reactivated = this.allModulesSignal().find(
            (m) => m.id === moduleId
          );
          if (reactivated) patched.push(reactivated);
        }
        return patched.filter(isModuleActive);
      });
    } catch (error) {
      console.error('Error updating module:', error);
      throw this.handleError(error, 'Error updating module');
    }
  }

  async deleteModule(schoolId: string, moduleId: string): Promise<void> {
    try {
      await this.updateModule(schoolId, moduleId, { isActive: false });
    } catch (error) {
      console.error('Error deactivating module:', error);
      throw this.handleError(error, 'Error deactivating module');
    }
  }

  async reactivateModule(schoolId: string, moduleId: string): Promise<void> {
    const allModules = this.allModules();
    const module = allModules.find((m) => m.id === moduleId);
    if (!module) {
      throw new Error('Das Modul wurde nicht gefunden.');
    }
    const conflict = findReactivationConflict(module, allModules);
    if (conflict) {
      throw new Error(
        `Modulnummer ${module.index} ist bereits vergeben. ` +
          'Bitte zuerst die Modulnummer dieses Moduls ändern.'
      );
    }

    // Lunch-Referenzen auf inzwischen gelöschte Konfigurationen bereinigen —
    // sonst blockiert eine tote mandatoryLunchId die Anmeldung (#517-Review).
    // Geprüft wird gegen die Schiene des Moduls, nicht gegen das Top-Level-Feld
    // der aktiven Schiene — sonst fallen Entwurfs-Konfigurationen weg (#1151).
    const school = this.schoolDetails();
    const lunchConfigurations =
      (module.schoolYear != null ? findPricing(school, module.schoolYear) : undefined)
        ?.lunchConfigurations ??
      school?.lunchConfigurations ??
      [];
    const staleLunchUpdates =
      school?.id === schoolId
        ? getStaleLunchReferenceUpdates(module, lunchConfigurations)
        : {};

    await this.updateModule(schoolId, moduleId, {
      isActive: true,
      ...staleLunchUpdates,
    });
  }

  async setSchoolStatus(schoolId: string, isActive: boolean): Promise<void> {
    try {
      await this.updateSchool(schoolId, { isActive });

      this.schoolsSignal.update((schools) =>
        schools.map((school) =>
          school.id === schoolId ? { ...school, isActive } : school
        )
      );
    } catch (error) {
      console.error('Error setting school status:', error);
      throw this.handleError(error, 'Error setting school status');
    }
  }

  async deleteSchool(schoolId: string): Promise<void> {
    try {
      // 1. Check for students enrolled at this school
      const studentsCollection = collection(this.firestore, 'students');
      const enrolledStudentsQuery = query(
        studentsCollection,
        where('schoolId', '==', schoolId),
        where('isActive', '==', true)
      );
      const enrolledStudentsSnapshot = await getDocs(enrolledStudentsQuery);

      if (!enrolledStudentsSnapshot.empty) {
        throw new Error(
          'Die Schule kann nicht gelöscht werden, da noch Schüler eingeschrieben sind. ' +
            'Bitte zuerst alle Schüler löschen oder übertragen.'
        );
      }

      // 2. Delete modules subcollection
      const modulesCollection = this.getModulesCollection(schoolId);
      const moduleSnapshot = await getDocs(modulesCollection);
      await Promise.all(moduleSnapshot.docs.map((d) => deleteDoc(d.ref)));

      // 3. Delete holidayConfigurations subcollection
      const holidayConfigsCollection = collection(
        this.schoolsCollection,
        schoolId,
        'holidayConfigurations'
      );
      const holidayConfigsSnapshot = await getDocs(holidayConfigsCollection);
      await Promise.all(
        holidayConfigsSnapshot.docs.map((d) => deleteDoc(d.ref))
      );

      // 3b. Delete holidayConfigurationDrafts subcollection (#1257)
      const draftConfigsSnapshot = await getDocs(
        collection(this.schoolsCollection, schoolId, 'holidayConfigurationDrafts')
      );
      await Promise.all(draftConfigsSnapshot.docs.map((d) => deleteDoc(d.ref)));

      // 4. Delete holidayCareRegistrations and their nested registrations
      const holidayCareCollection = collection(
        this.schoolsCollection,
        schoolId,
        'holidayCareRegistrations'
      );
      const holidayCareSnapshot = await getDocs(holidayCareCollection);

      for (const yearVacDoc of holidayCareSnapshot.docs) {
        // Delete nested registrations subcollection
        const registrationsCollection = collection(
          yearVacDoc.ref,
          'registrations'
        );
        const registrationsSnapshot = await getDocs(registrationsCollection);
        await Promise.all(
          registrationsSnapshot.docs.map((regDoc) => deleteDoc(regDoc.ref))
        );

        // Delete the year-vacation document
        await deleteDoc(yearVacDoc.ref);
      }

      // 5. Delete the school document
      const schoolRef = doc(this.schoolsCollection, schoolId);
      await deleteDoc(schoolRef);

      // Update local signals
      this.schoolsSignal.update((schools) =>
        schools.filter((school) => school.id !== schoolId)
      );
      this.modulesSignal.set([]);
      this.allModulesSignal.set([]);

      const currentSchool = this.schoolDetails();
      if (currentSchool?.id === schoolId) {
        this.schoolDetailsSignal.set(undefined);
      }

      console.log('School deleted with all subcollections:', schoolId);
    } catch (error) {
      console.error('Error deleting school:', error);
      throw this.handleError(error, 'Error deleting school');
    }
  }
}